import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { Phone, PhoneIncoming, PhoneOutgoing, Download } from 'lucide-react';

export default function CallHistoryPage() {
  const [calls, setCalls] = useState([]);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ direction: '', status: '' });
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadCalls(); }, [filters]);

  const loadCalls = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: 100, ...Object.fromEntries(Object.entries(filters).filter(([,v]) => v)) });
      const res = await api.get(`/calls?${params}`);
      setCalls(res.data.calls || []);
      setTotal(res.data.total || 0);
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const exportCSV = () => {
    const headers = ['Date', 'Agent', 'Caller', 'Direction', 'Status', 'Duration (s)', 'Cost'];
    const rows = calls.map(c => [
      new Date(c.started_at * 1000).toISOString(),
      c.agent_name, c.caller_number, c.direction, c.status, c.duration_seconds, c.cost
    ]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'calls.csv'; a.click();
  };

  return (
    <div className="page">
      <div className="page-header">
        <h1>Call History</h1>
        <button className="btn btn-ghost" onClick={exportCSV}>
          <Download size={16} /> Export CSV
        </button>
      </div>

      <div className="filter-bar">
        <select className="input input--sm" value={filters.direction} onChange={e => setFilters(f => ({ ...f, direction: e.target.value }))}>
          <option value="">All Directions</option>
          <option value="inbound">Inbound</option>
          <option value="outbound">Outbound</option>
        </select>
        <select className="input input--sm" value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}>
          <option value="">All Statuses</option>
          <option value="completed">Completed</option>
          <option value="failed">Failed</option>
          <option value="missed">Missed</option>
          <option value="voicemail">Voicemail</option>
        </select>
        <span className="filter-count">{total} calls</span>
      </div>

      {loading ? (
        <div className="page-loading"><div className="spinner" /></div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Date/Time</th>
              <th>Agent</th>
              <th>Caller</th>
              <th>Direction</th>
              <th>Duration</th>
              <th>Status</th>
              <th>Cost</th>
            </tr>
          </thead>
          <tbody>
            {calls.length === 0 ? (
              <tr><td colSpan={7} className="empty-row">No calls found</td></tr>
            ) : calls.map(call => (
              <tr key={call.id}>
                <td>{call.started_at ? new Date(call.started_at * 1000).toLocaleString() : '—'}</td>
                <td>{call.agent_name || '—'}</td>
                <td>{call.caller_number || 'Unknown'}</td>
                <td>
                  <span className={`badge badge--${call.direction}`}>
                    {call.direction === 'inbound' ? <PhoneIncoming size={12} /> : <PhoneOutgoing size={12} />}
                    {call.direction}
                  </span>
                </td>
                <td>{Math.floor((call.duration_seconds || 0) / 60)}m {(call.duration_seconds || 0) % 60}s</td>
                <td><span className={`status-badge status-badge--${call.status}`}>{call.status}</span></td>
                <td>${(call.cost || 0).toFixed(4)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
